import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {
  ConfiguracaoBalanca, configuracaoBalancaValida, configuracaoPadraoBalanca,
  ErroPesagemBalanca, PERFIS_BALANCA, pesoBalancaEmKg, ProtocoloBalanca
} from './protocolo-balanca';

@Injectable({providedIn: 'root'})
export class BalancaSerialService implements OnDestroy {
  private readonly chavePreferencias = 'pdv.balanca.serial.v1';
  private preferencias = configuracaoPadraoBalanca('urano');
  get configuracao(): ConfiguracaoBalanca { return {...this.preferencias}; }
  get perfil() { return PERFIS_BALANCA[this.preferencias.modelo]; }
  get suportada(): boolean { return typeof navigator !== 'undefined' && !!(navigator as any).serial; }
  get status(): string {
    if (this.ocupada) return this.conectada ? 'Aguardando leitura' : 'Conectando';
    if (this.erro) return this.precisaSilencio && this.conectada
      ? (this.diagnostico?.bytesRecebidos ? 'Resposta não reconhecida' : 'Sem resposta') : 'Falha na comunicação';
    if (!this.conectada) return 'Desconectada';
    if (this.avisoLeitura) return this.avisoLeitura.status;
    return this.comunicacaoValidada ? 'Leitura recebida' : 'Porta aberta · aguarda teste';
  }

  conectada = false;
  ocupada = false;
  comunicacaoValidada = false;
  ultimaLeitura: {pesoKg: number; horario: Date} = null;
  erro = '';
  avisoLeitura: ErroPesagemBalanca = null;
  avisoPreferencias = '';
  diagnostico: {
    horario: Date; parametros: string; comandoHex: string; comandoEnviado: boolean; bytesRecebidos: number;
    amostraHex: string
  } = null;
  private porta: any;
  private leitor: any;
  private leitura: Promise<void>;
  private escrita: Promise<void> = Promise.resolve();
  private fechamento: Promise<void>;
  private protocolo = new ProtocoloBalanca();
  private pendente: {resolve: (peso: number) => void; reject: (erro: Error) => void; permitirZero: boolean} = null;
  private destruida = false;
  private desconectando = false;
  private reconexaoTentada = false;
  private precisaSilencio = false;
  private ultimoByteEm = 0;

  constructor(private zone: NgZone) {
    try {
      const salva = localStorage.getItem(this.chavePreferencias);
      if (salva) {
        const config = JSON.parse(salva);
        if (configuracaoBalancaValida(config)) this.preferencias = config;
        else this.avisoPreferencias = 'Preferências inválidas. Revise e salve a configuração novamente.';
      }
    } catch (erro) {
      this.avisoPreferencias = 'Não foi possível recuperar as preferências deste navegador.';
    }
  }

  salvarConfiguracao(config: ConfiguracaoBalanca): void {
    if (this.conectada || this.ocupada) throw new Error('Desconecte a balança antes de alterar a configuração.');
    if (!configuracaoBalancaValida(config)) throw new Error('Revise os parâmetros da conexão serial.');
    this.preferencias = {...config};
    this.ultimaLeitura = null;
    this.diagnostico = null;
    this.comunicacaoValidada = false;
    this.erro = '';
    this.avisoLeitura = null;
    try {
      localStorage.setItem(this.chavePreferencias, JSON.stringify(this.preferencias));
      this.avisoPreferencias = '';
    } catch (erro) {
      this.avisoPreferencias = 'Configuração aplicada nesta sessão. O navegador não permitiu salvá-la para o próximo acesso.';
    }
  }

  async reconectarAutomaticamente(): Promise<void> {
    if (this.reconexaoTentada || !this.suportada) return;
    this.reconexaoTentada = true;
    try { await this.abrirConexao('automatica'); }
    catch (erro) { /* Diagnóstico visível; uma nova tentativa depende do operador. */ }
  }

  conectar(escolherPorta = false): Promise<void> {
    return this.abrirConexao(escolherPorta ? 'selecionar' : 'reutilizar');
  }

  private async abrirConexao(modo: 'automatica' | 'reutilizar' | 'selecionar'): Promise<void> {
    if (this.ocupada || this.conectada || this.destruida) return;
    if (!this.suportada) throw new Error('Use Chrome ou Edge no computador, com o sistema aberto por HTTPS ou localhost.');
    this.reconexaoTentada = true;
    this.desconectando = false;
    this.ocupada = true;
    this.erro = '';
    this.avisoLeitura = null;
    this.ultimaLeitura = null;
    this.diagnostico = null;
    this.comunicacaoValidada = false;
    try {
      const serial = (navigator as any).serial;
      const autorizadas = modo === 'selecionar' ? [] : await serial.getPorts();
      // Não escolher arbitrariamente quando há vários equipamentos autorizados.
      if (modo === 'automatica' && autorizadas.length !== 1) return;
      const porta = autorizadas.length === 1 ? autorizadas[0] : await serial.requestPort();
      await this.fechamento;
      await this.leitura;
      if (this.destruida || this.desconectando) return;
      const {modelo, ...opcoes} = this.preferencias;
      await porta.open({...opcoes, flowControl: 'none'});
      if (this.destruida || this.desconectando) { await porta.close(); return; }
      this.zone.run(() => {
        this.desconectando = false;
        this.porta = porta;
        this.conectada = true;
        this.precisaSilencio = false;
        this.protocolo = new ProtocoloBalanca();
        this.leitura = this.receba(porta);
      });
    } catch (erro) {
      if (erro?.name !== 'NotFoundError') this.zone.run(() => {
        this.erro = 'Não foi possível abrir a porta. Confira o cabo e se outro programa está usando a balança.';
      });
      throw erro;
    } finally {
      this.zone.run(() => this.ocupada = false);
    }
  }

  private async receba(porta: any): Promise<void> {
    const leitor = porta.readable.getReader();
    this.leitor = leitor;
    try {
      while (true) {
        const {value, done} = await leitor.read();
        if (done) break;
        if (value.length) this.ultimoByteEm = Date.now();
        this.zone.run(() => {
          if (this.pendente && this.diagnostico) {
            const diagnostico = this.diagnostico;
            const restantes = Math.max(0, 32 - diagnostico.bytesRecebidos);
            const hex = Array.from(value.slice(0, restantes) as Uint8Array)
              .map(byte => byte.toString(16).padStart(2, '0').toUpperCase()).join(' ');
            diagnostico.amostraHex = [diagnostico.amostraHex, hex].filter(Boolean).join(' ');
            diagnostico.bytesRecebidos += value.length;
          }
          for (const resposta of this.protocolo.receba(value)) {
            if (!this.pendente) continue;
            const pendente = this.pendente;
            this.pendente = null;
            try {
              const peso = pesoBalancaEmKg(resposta, this.preferencias.modelo, pendente.permitirZero);
              this.ultimaLeitura = {pesoKg: peso, horario: new Date()};
              this.comunicacaoValidada = true;
              pendente.resolve(peso);
            } catch (erro) {
              this.comunicacaoValidada = erro instanceof ErroPesagemBalanca;
              pendente.reject(erro);
            }
          }
        });
      }
    } catch (erro) {
      // A remoção do cabo e falhas seriais terminam no mesmo estado abaixo.
    } finally {
      leitor.releaseLock();
      this.zone.run(() => {
        this.leitor = null;
        this.conectada = false;
        this.comunicacaoValidada = false;
        this.avisoLeitura = null;
        if (!this.desconectando) this.erro = 'Conexão com a balança interrompida. Confira o cabo e conecte novamente.';
        if (this.pendente) {
          this.pendente.reject(new Error('Conexão com a balança encerrada. Conecte novamente.'));
          this.pendente = null;
        }
      });
      await this.escrita;
      try { await porta.close(); } catch (erro) { /* Porta removida fisicamente. */ }
      this.porta = null;
    }
  }

  async lerPeso(permitirZero = false): Promise<number> {
    if (!this.conectada || this.ocupada) throw new Error('Conecte a balança e aguarde a operação atual.');
    this.ocupada = true;
    this.erro = '';
    this.avisoLeitura = null;
    let timer: any;
    try {
      await this.escrita;
      if (this.precisaSilencio) await this.aguardeSilencio();
      if (!this.conectada) throw new Error('Conexão com a balança encerrada. Conecte novamente.');
      this.protocolo = new ProtocoloBalanca();
      this.precisaSilencio = false;
      const {baudRate, dataBits, parity, stopBits} = this.preferencias;
      const diagnostico: BalancaSerialService['diagnostico'] = this.diagnostico = {
        horario: new Date(), parametros: `${baudRate} baud · ${dataBits} bits · paridade ${parity} · ${stopBits} parada(s)`,
        comandoHex: this.perfil.requisicao.map(byte => byte.toString(16).padStart(2, '0').toUpperCase()).join(' '),
        comandoEnviado: false, bytesRecebidos: 0, amostraHex: ''
      };
      return await new Promise<number>((resolve, reject) => {
        this.pendente = {resolve, reject, permitirZero};
        timer = setTimeout(() => {
          this.pendente = null;
          this.precisaSilencio = true;
          reject(new Error(diagnostico.bytesRecebidos
            ? 'Chegaram dados pela porta, mas não uma resposta no formato esperado. Confira ' + this.perfil.configuracao + ' na balança e os parâmetros em Avançado.'
            : 'Nenhum dado recebido pela porta. Confira a porta escolhida, o cabo e ' + this.perfil.configuracao + ' na balança. Aguarde o peso estabilizar e teste novamente.'));
        }, 3000);
        const escritor = this.porta.writable.getWriter();
        this.escrita = escritor.write(new Uint8Array(this.perfil.requisicao)).then(() => {
          this.zone.run(() => diagnostico.comandoEnviado = true);
        }).catch(() => {
          reject(new Error('Não foi possível solicitar o peso. Reconecte a balança.'));
          void this.desconectar();
        }).finally(() => escritor.releaseLock());
      });
    } catch (erro) {
      this.zone.run(() => {
        if (erro instanceof ErroPesagemBalanca && this.conectada) this.avisoLeitura = erro;
        else {
          this.comunicacaoValidada = false;
          this.erro = erro.message || 'Não foi possível ler o peso.';
        }
      });
      throw erro;
    } finally {
      clearTimeout(timer);
      this.pendente = null;
      this.zone.run(() => this.ocupada = false);
    }
  }

  private async aguardeSilencio(): Promise<void> {
    const inicio = Date.now();
    while (true) {
      if (!this.conectada) throw new Error('Conexão com a balança encerrada. Conecte novamente.');
      const agora = Date.now();
      const restante = 300 - (agora - Math.max(inicio, this.ultimoByteEm));
      if (restante <= 0) return;
      if (agora - inicio >= 3000) throw new Error('A balança envia dados continuamente. Confira o protocolo por demanda e tente novamente.');
      await new Promise(resolve => setTimeout(resolve, Math.min(restante, 3000 - (agora - inicio))));
    }
  }

  async desconectar(): Promise<void> {
    this.reconexaoTentada = true;
    if (this.fechamento) return this.fechamento;
    this.desconectando = true;
    this.conectada = false;
    this.comunicacaoValidada = false;
    this.avisoLeitura = null;
    this.erro = '';
    this.fechamento = (async () => {
      if (this.leitor) {
        try { await this.leitor.cancel(); } catch (erro) { /* Leitura já encerrada. */ }
      }
      await this.leitura;
    })();
    try { await this.fechamento; }
    finally { this.fechamento = null; }
  }

  ngOnDestroy(): void {
    this.destruida = true;
    void this.desconectar();
  }
}
